import { ApolloError } from "@apollo/client";
import { GraphQLError, GraphQLFormattedError } from "graphql";
import { forOwn } from "lodash";
import { useEffect } from "react";
import { ErrorOption, UseFormSetError } from "react-hook-form";

import ValidationError from "../models/FormValidationError";

type GQLValidationError = {
    property: string;
    children: GQLValidationError[];
    constraints?: Record<string, string>;
};

type ErrorResult = {
    errors?: GraphQLError[];
};

function mapValidationErrorToMessages(error: GQLValidationError): Record<string, ErrorOption> {
    const fieldErrors = error.constraints
        ? {
              [error.property]: {
                  message: Object.values(error.constraints)[0],
              },
          }
        : undefined;

    const childErrors = error.children
        ? error.children.map(mapValidationErrorToMessages).reduce((result, current) => ({ ...result, ...current }), {})
        : undefined;

    return {
        ...fieldErrors,
        ...childErrors,
    };
}

function handleValidationError(error: GraphQLFormattedError): GraphQLError | undefined {
    const validationErrors = error.extensions?.validationErrors as GQLValidationError[] | undefined;

    if (!validationErrors?.length) return new GraphQLError(error.message);

    const messages = validationErrors.map(mapValidationErrorToMessages);

    return new ValidationError(messages.reduce((current, out) => ({ ...out, ...current }), {}));
}

function mapGraphQlValidationError(error: Error) {
    const apolloError = error as ApolloError;
    if (!apolloError.cause) return error;
    const cause = apolloError.cause as GraphQLFormattedError | null;

    if (cause && "extensions" in cause && cause?.extensions?.code === "BAD_USER_INPUT") {
        return handleValidationError(cause);
    } else if (cause && "result" in cause) {
        const result = cause.result as ErrorResult;
        if (result.errors && result.errors.length > 0) {
            return result.errors[0];
        }
    }

    return error;
}

export default function useApplyErrorToForm<T extends object>(
    error: Error | null | undefined,
    setError: UseFormSetError<T>,
) {
    useEffect(() => {
        if (!error) return;
        const mappedError = mapGraphQlValidationError(error);
        if (!mappedError) return;

        setError("root", mappedError);
        if (mappedError instanceof ValidationError && mappedError.errors) {
            forOwn(mappedError.errors, (fieldErrors, name) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setError(name as any, fieldErrors);
            });
        }
    }, [error, setError]);
}
